import { Link } from '@tanstack/react-router';
import { Inbox, SearchX, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface EmptyStateAction {
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: 'primary' | 'ghost';
}

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  primaryAction?: EmptyStateAction;
  secondaryLink?: { label: string; href: string };
  children?: ReactNode;
  className?: string;
}

function ActionButton({ action }: { action: EmptyStateAction }) {
  const variant = action.variant === 'ghost' ? 'ghost' : 'primary';
  if (action.to) {
    return (
      <Button asChild variant={variant} size="md">
        <Link to={action.to as never}>{action.label}</Link>
      </Button>
    );
  }
  return (
    <Button variant={variant} size="md" onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryLink,
  children,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'mx-auto flex max-w-md flex-col items-center gap-4 py-20 text-center',
        className,
      )}
    >
      <div className="bg-primary-50 text-primary-500 ring-primary-100 flex h-16 w-16 items-center justify-center rounded-2xl ring-1">
        <Icon className="h-7 w-7" strokeWidth={1.5} aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-1.5">
        <h3 className="font-display text-title-md text-accent-ink leading-tight italic">{title}</h3>
        {description ? (
          <p className="text-body-sm leading-relaxed text-neutral-500">{description}</p>
        ) : null}
      </div>

      {primaryAction ? (
        <div className="mt-1">
          <ActionButton action={primaryAction} />
        </div>
      ) : null}

      {secondaryLink ? (
        <a
          href={secondaryLink.href}
          className="text-meta-sm text-neutral-500 hover:text-neutral-900"
        >
          {secondaryLink.label}
        </a>
      ) : null}

      {children}
    </div>
  );
}

EmptyState.NoData = function NoData(props: Omit<EmptyStateProps, 'icon'>) {
  return <EmptyState icon={Inbox} {...props} />;
};

EmptyState.NoResults = function NoResults(props: Omit<EmptyStateProps, 'icon'>) {
  return <EmptyState icon={SearchX} {...props} />;
};

EmptyState.Hub = function Hub({
  title,
  description,
  secondaryLink,
}: {
  title: string;
  description?: string;
  secondaryLink?: { label: string; href: string };
}) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-24 text-center">
      <h2 className="font-display text-display-xl text-accent-ink leading-[1.2] tracking-[-0.02em]">
        {title}
      </h2>
      {description ? <p className="text-body-md text-neutral-500">{description}</p> : null}
      {secondaryLink ? (
        <a
          href={secondaryLink.href}
          className="text-body-md text-primary-500 hover:text-primary-400 font-medium"
        >
          {secondaryLink.label}
        </a>
      ) : null}
    </div>
  );
};
